import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, MapPinIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { FaFacebook } from 'react-icons/fa';

export default function CampusCard({ campus }) {
  const [idx, setIdx] = useState(0);
  const [direction, setDirection] = useState(1);
  const images = campus.images;

  const go = (dir) => {
    setDirection(dir);
    setIdx((prev) => (prev + dir + images.length) % images.length);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm"
    >
      {/* ── HEADER ── */}
      <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-gray-100">
        {/* Nombre */}
        <h3 className="font-black text-black text-lg leading-none flex-shrink-0">
          {campus.nombre}
        </h3>

        {/* Dirección */}
        <p className="text-gray-400 text-xs leading-tight flex-1 min-w-0 truncate hidden sm:block">
          {campus.direccion}
        </p>

        {/* Acciones */}
        <div className="flex items-center gap-3 flex-shrink-0 ml-auto">
          {/* Facebook */}
          <a
            href={campus.fb}
            target="_blank"
            rel="noreferrer"
            aria-label="Facebook"
            className="text-gray-400 hover:text-blue-600 transition-colors"
          >
            <FaFacebook size={18} />
          </a>

          {/* Botón VISÍTANOS */}
          <a
            href={campus.linkMaps}
            target="_blank"
            rel="noreferrer"
            className="px-4 py-1.5 text-black text-xs font-bold rounded-full
                       hover:brightness-110 hover:scale-105 transition-all duration-200 whitespace-nowrap"
            style={{ background: 'linear-gradient(135deg, #FFE033, #FFB800)' }}
          >
            ¡VISÍTANOS!
          </a>

          {/* Pin mapa */}
          <a
            href={campus.linkMaps}
            target="_blank"
            rel="noreferrer"
            aria-label="Ver en Google Maps"
            className="text-gray-400 hover:text-red-500 transition-colors"
          >
            <MapPinIcon className="w-5 h-5" />
          </a>
        </div>
      </div>

      {/* ── CARRUSEL ── */}
      <div className="relative w-full overflow-hidden bg-gray-100" style={{ aspectRatio: '16/9' }}>
        <AnimatePresence initial={false} custom={direction}>
          {images[idx] ? (
            <motion.img
              key={idx}
              custom={direction}
              src={images[idx]}
              alt={`${campus.nombre} foto ${idx + 1}`}
              variants={{
                enter: (d) => ({ x: d > 0 ? '100%' : '-100%', opacity: 0 }),
                center: { x: 0, opacity: 1 },
                exit: (d) => ({ x: d > 0 ? '-100%' : '100%', opacity: 0 }),
              }}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.35, ease: 'easeInOut' }}
              className="absolute inset-0 w-full h-full object-cover"
              draggable={false}
            />
          ) : (
            <motion.div
              key={`placeholder-${idx}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 w-full h-full bg-gray-100 flex flex-col items-center justify-center gap-2"
            >
              <PhotoIcon className="w-10 h-10 text-gray-300" />
              <span className="text-gray-300 text-xs tracking-widest uppercase">Foto próximamente</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Flechas */}
        {images.length > 1 && (
          <>
            <button
              onClick={() => go(-1)}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-10
                         w-8 h-8 rounded-full bg-black/40 hover:bg-black/70 text-white
                         flex items-center justify-center transition-colors"
              aria-label="Anterior"
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => go(1)}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10
                         w-8 h-8 rounded-full bg-black/40 hover:bg-black/70 text-white
                         flex items-center justify-center transition-colors"
              aria-label="Siguiente"
            >
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </>
        )}

        {/* Dots */}
        {images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => { setDirection(i > idx ? 1 : -1); setIdx(i); }}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === idx ? 'w-5 bg-white' : 'w-1.5 bg-white/50'
                }`}
                aria-label={`Ir a foto ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
